import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api/client';
import appsService from '../services/apps';
import oauthConfigService from '../services/oauthConfig';
import ssoService from '../services/sso';
import tenantsService from '../services/tenants';
import { encryptPassword } from '../utils/crypto';
import { setRefreshToken, setToken } from '../utils/storage';

// ─── Social SDK Loaders ───────────────────────────────────────────────────────

/**
 * Dynamically loads the Google Identity Services script.
 * Returns a promise that resolves once window.google is available.
 */
function loadGoogleSDK() {
    return new Promise((resolve, reject) => {
        if (window.google?.accounts) return resolve();
        if (document.getElementById('google-gsi-script')) {
            // Script tag exists but may not have loaded yet
            const existing = document.getElementById('google-gsi-script');
            existing.addEventListener('load', resolve);
            existing.addEventListener('error', reject);
            return;
        }
        const script = document.createElement('script');
        script.id = 'google-gsi-script';
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = () => reject(new Error('Failed to load Google Identity Services SDK'));
        document.head.appendChild(script);
    });
}

/**
 * Dynamically loads Apple's Sign In JS SDK.
 * Returns a promise that resolves once window.AppleID is available.
 */
function loadAppleSDK() {
    return new Promise((resolve, reject) => {
        if (window.AppleID) return resolve();
        if (document.getElementById('apple-sign-in-script')) {
            const existing = document.getElementById('apple-sign-in-script');
            existing.addEventListener('load', resolve);
            existing.addEventListener('error', reject);
            return;
        }
        const script = document.createElement('script');
        script.id = 'apple-sign-in-script';
        script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = () => reject(new Error('Failed to load Apple Sign In SDK'));
        document.head.appendChild(script);
    });
}

// ─── Component ───────────────────────────────────────────────────────────────

const LoginPage = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [appId, setAppId] = useState('');
    const [tenantId, setTenantId] = useState('');
    const [loading, setLoading] = useState(false);
    const [socialLoading, setSocialLoading] = useState(''); // provider name while loading
    const [error, setError] = useState('');
    const [showOTP, setShowOTP] = useState(false);
    const [otp, setOtp] = useState('');
    const [authNToken, setAuthNToken] = useState('');
    const [otpSent, setOtpSent] = useState(false);

    // TOTP state
    const [showTotp, setShowTotp] = useState(false);
    const [totpCode, setTotpCode] = useState('');

    // Social login state
    const [socialProviders, setSocialProviders] = useState([]); // [{ provider, client_id }]
    const [sdksReady, setSdksReady] = useState({ google: false, apple: false });

    // SSO (enterprise) login state
    const [ssoProviders, setSsoProviders] = useState([]); // [{ sso_config_id, provider_name, provider_slug, protocol }]

    // Multi-tenant: available tenants for the app (public list) → login dropdown
    const [tenants, setTenants] = useState([]); // [{ tenant_id, tenant_name }]

    // App picker: public app list → login dropdown (label = name, value = app_id)
    const [apps, setApps] = useState([]); // [{ app_id, app_name }]

    const navigate = useNavigate();
    const location = useLocation();
    const sessionReason = new URLSearchParams(location.search).get('reason');
    const sessionNotice =
        sessionReason === 'timeout'
            ? 'You were signed out after a period of inactivity. Sign in again to continue.'
            : sessionReason === 'ended'
            ? 'Your session has ended. Sign in again to continue.'
            : '';

    // Load the public app list once for the App dropdown.
    useEffect(() => {
        appsService
            .getPublicList()
            .then(setApps)
            .catch(() => setApps([]));
    }, []);

    // ── Fetch social config when app_id is populated ─────────────────────────

    const fetchSocialConfig = useCallback(async (id) => {
        if (!id || !id.trim()) {
            setSocialProviders([]);
            setSsoProviders([]);
            setTenants([]);
            return;
        }
        try {
            const configs = await oauthConfigService.getSocialConfig(id.trim());
            setSocialProviders(configs);
        } catch {
            setSocialProviders([]);
        }
        // Also fetch enterprise SSO providers
        try {
            const sso = await ssoService.getProviders(id.trim());
            console.log('Fetched SSO providers:', sso);
            setSsoProviders(sso);
        } catch {
            setSsoProviders([]);
        }
        // Available tenants (multi-tenant apps only) → login dropdown
        try {
            const list = await tenantsService.getPublicList(id.trim());
            setTenants(list);
        } catch {
            setTenants([]);
        }
    }, []);

    // Debounce app_id changes to avoid spamming the API while the user types
    useEffect(() => {
        const timer = setTimeout(() => fetchSocialConfig(appId), 500);
        return () => clearTimeout(timer);
    }, [appId, fetchSocialConfig]);

    // ── Load provider SDKs once social config is fetched ─────────────────────

    useEffect(() => {
        socialProviders.forEach(({ provider, client_id }) => {
            if (provider === 'google' && !sdksReady.google) {
                loadGoogleSDK()
                    .then(() => {
                        window.google.accounts.id.initialize({
                            client_id,
                            callback: (response) => handleGoogleCallback(response.credential),
                            auto_select: false,
                            cancel_on_tap_outside: true,
                        });
                        setSdksReady((prev) => ({ ...prev, google: true }));
                    })
                    .catch((err) => console.error('Google SDK load error:', err));
            }

            if (provider === 'apple' && !sdksReady.apple) {
                loadAppleSDK()
                    .then(() => {
                        window.AppleID.auth.init({
                            clientId: client_id,
                            scope: 'name email',
                            redirectURI: window.location.origin + window.location.pathname,
                            usePopup: true,
                        });
                        setSdksReady((prev) => ({ ...prev, apple: true }));
                    })
                    .catch((err) => console.error('Apple SDK load error:', err));
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [socialProviders]);

    // ── Token helpers ─────────────────────────────────────────────────────────

    const handleAuthSuccess = (token, refreshToken) => {
        setToken(token);
        if (refreshToken) setRefreshToken(refreshToken);
        const target = location.state?.from?.pathname || '/dashboard';
        navigate(target, { replace: true });
    };

    // ── Social login flows ────────────────────────────────────────────────────

    const handleGoogleCallback = async (idToken) => {
        setSocialLoading('google');
        setError('');
        try {
            const data = await oauthConfigService.socialLogin({
                provider: 'google',
                id_token: idToken,
                app_id: appId || undefined,
            });
            handleAuthSuccess(data.token, data.refreshToken);
        } catch (err) {
            setError(err?.response?.data?.message || 'Google sign-in failed. Please try again.');
        } finally {
            setSocialLoading('');
        }
    };

    const handleGoogleSignIn = () => {
        if (!window.google?.accounts) return;
        setError('');
        window.google.accounts.id.prompt((notification) => {
            if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
                // Fallback: open the popup manually
                window.google.accounts.id.prompt();
            }
        });
    };

    const handleAppleSignIn = async () => {
        if (!window.AppleID) return;
        setSocialLoading('apple');
        setError('');
        try {
            const response = await window.AppleID.auth.signIn();
            const { id_token } = response.authorization;
            // Apple only sends name on first login — extract from response if present
            const apple_user = response.user
                ? { firstName: response.user.name?.firstName, lastName: response.user.name?.lastName }
                : undefined;

            const data = await oauthConfigService.socialLogin({
                provider: 'apple',
                id_token,
                app_id: appId || undefined,
                ...(apple_user && { apple_user }),
            });
            handleAuthSuccess(data.token, data.refreshToken);
        } catch (err) {
            if (err?.error === 'popup_closed_by_user') {
                // User dismissed — not an error
            } else {
                setError(err?.response?.data?.message || 'Apple sign-in failed. Please try again.');
            }
        } finally {
            setSocialLoading('');
        }
    };

    // ── Password login ────────────────────────────────────────────────────────

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const encryptedPassword = encryptPassword(password);
            // Mark this as a UM-console login so the backend knows UM owns OTP
            // delivery (vs external apps logging in via API, where the app does).
            const loginPayload = { username, password: encryptedPassword, login_type: 'UM_PORTAL' };
            if (appId) loginPayload.app_id = appId;
            if (tenantId) loginPayload.tenant_id = tenantId;

            const res = await api.post('/api/auth/login', loginPayload);
            const token = res?.data?.data?.token;
            const refreshToken = res?.data?.data?.refreshToken;

            if (token && refreshToken) {
                handleAuthSuccess(token, refreshToken);
                return;
            }

            if (token && res?.data?.data?.requires_totp) {
                setAuthNToken(token);
                setShowTotp(true);
            } else if (token) {
                setAuthNToken(token);
                setToken(token);
                await handleRequestOTP(token);
            } else {
                setError('No token received. Check credentials.');
            }
        } catch (err) {
            if (
                err?.response?.status === 303 &&
                err?.response?.data?.data?.redirect === '/authentication/reset-password'
            ) {
                const resetToken = err?.response?.data?.data?.token;
                navigate('/reset-password', { state: { token: resetToken } });
                return;
            }
            setError(err?.response?.data?.message || 'Login failed');
        } finally {
            setLoading(false);
        }
    };

    const handleRequestOTP = async (token) => {
        try {
            await api.post(
                '/api/auth/twoFactorAuthOTP',
                {},
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            setShowOTP(true);
            setOtpSent(true);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to send OTP');
        }
    };

    const handleVerifyOTP = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const res = await api.post(
                '/api/auth/verifyEmailOtp',
                { code: otp },
                { headers: { Authorization: `Bearer ${authNToken}` } }
            );
            const token = res?.data?.data?.token;
            const refreshToken = res?.data?.data?.refreshToken;
            if (token) {
                handleAuthSuccess(token, refreshToken);
            } else {
                setError('No token received after OTP verification.');
            }
        } catch (err) {
            setError(err?.response?.data?.message || 'OTP verification failed');
        } finally {
            setLoading(false);
        }
    };

    // ── TOTP verification ─────────────────────────────────────────────────────

    const handleVerifyTotp = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const res = await api.post(
                '/api/auth/verify-totp',
                { totp_code: totpCode },
                { headers: { Authorization: `Bearer ${authNToken}` } }
            );
            const token = res?.data?.data?.token;
            const refreshToken = res?.data?.data?.refreshToken;
            if (token) {
                handleAuthSuccess(token, refreshToken);
            } else {
                setError('No token received after TOTP verification.');
            }
        } catch (err) {
            setError(err?.response?.data?.message || 'TOTP verification failed');
        } finally {
            setLoading(false);
        }
    };

    // ── TOTP screen ──────────────────────────────────────────────────────────

    if (showTotp) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
                <div className="w-full max-w-md bg-white shadow-sm rounded-2xl p-8 border border-slate-200">
                    <div className="mb-6">
                        <h1 className="text-2xl font-bold text-slate-900">Authenticator Verification</h1>
                        <p className="text-sm text-slate-500 mt-1">
                            Enter the 6-digit code from your authenticator app to complete login.
                        </p>
                    </div>
                    <form className="space-y-4" onSubmit={handleVerifyTotp}>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">TOTP Code</label>
                            <input
                                type="text"
                                required
                                value={totpCode}
                                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-center text-lg tracking-widest focus:outline-none focus:ring-2 focus:ring-primary-200"
                                placeholder="000000"
                                maxLength="6"
                            />
                        </div>
                        {error && (
                            <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                                {error}
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={loading || totpCode.length !== 6}
                            className="w-full rounded-lg bg-slate-900 text-white font-semibold py-2.5 hover:bg-slate-800 disabled:opacity-60"
                        >
                            {loading ? 'Verifying...' : 'Verify'}
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setShowTotp(false);
                                setTotpCode('');
                                setAuthNToken('');
                            }}
                            className="w-full rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                        >
                            Back to Login
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    // ── 2FA screen ────────────────────────────────────────────────────────────

    if (showOTP) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
                <div className="w-full max-w-md bg-white shadow-sm rounded-2xl p-8 border border-slate-200">
                    <div className="mb-6">
                        <h1 className="text-2xl font-bold text-slate-900">Two-Factor Authentication</h1>
                        <p className="text-sm text-slate-500 mt-1">
                            {otpSent ? 'Enter the OTP sent to your email to complete login.' : 'Requesting OTP...'}
                        </p>
                    </div>
                    <form className="space-y-4" onSubmit={handleVerifyOTP}>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">OTP Code</label>
                            <input
                                type="text"
                                required
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-center text-lg tracking-widest focus:outline-none focus:ring-2 focus:ring-primary-200"
                                placeholder="000000"
                                maxLength="6"
                            />
                        </div>
                        {error && (
                            <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                                {error}
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={loading || !otpSent}
                            className="w-full rounded-lg bg-slate-900 text-white font-semibold py-2.5 hover:bg-slate-800 disabled:opacity-60"
                        >
                            {loading ? 'Verifying...' : 'Verify OTP'}
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setShowOTP(false);
                                setOtp('');
                                setAuthNToken('');
                                setOtpSent(false);
                            }}
                            className="w-full rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                        >
                            Back to Login
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    // ── Main login screen ─────────────────────────────────────────────────────

    const googleConfig = socialProviders.find((p) => p.provider === 'google');
    const appleConfig = socialProviders.find((p) => p.provider === 'apple');
    const hasSocialProviders = !!googleConfig || !!appleConfig;

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
            <div className="w-full max-w-md bg-white shadow-sm rounded-2xl p-8 border border-slate-200">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-slate-900">User Management</h1>
                    <p className="text-sm text-slate-500 mt-1">Sign in to view apps, users, roles, and components.</p>
                </div>

                {location.state?.signedOut && (
                    <p
                        role="status"
                        className="mb-5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    >
                        You’ve signed out.
                    </p>
                )}
                {!location.state?.signedOut && sessionNotice && (
                    <p
                        role="status"
                        className="mb-5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
                    >
                        {sessionNotice}
                    </p>
                )}

                {/* Social login buttons — shown only when app_id has enabled social providers */}
                {hasSocialProviders && (
                    <div className="space-y-2 mb-5">
                        {googleConfig && (
                            <button
                                type="button"
                                onClick={handleGoogleSignIn}
                                disabled={!!socialLoading || !sdksReady.google}
                                className="w-full flex items-center justify-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60 transition-colors"
                            >
                                {socialLoading === 'google' ? (
                                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-400 border-t-transparent" />
                                ) : (
                                    <GoogleIcon />
                                )}
                                {sdksReady.google ? 'Continue with Google' : 'Loading Google...'}
                            </button>
                        )}

                        {appleConfig && (
                            <button
                                type="button"
                                onClick={handleAppleSignIn}
                                disabled={!!socialLoading || !sdksReady.apple}
                                className="w-full flex items-center justify-center gap-3 rounded-lg border border-slate-900 bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60 transition-colors"
                            >
                                {socialLoading === 'apple' ? (
                                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                ) : (
                                    <AppleIcon />
                                )}
                                {sdksReady.apple ? 'Continue with Apple' : 'Loading Apple...'}
                            </button>
                        )}

                        <div className="flex items-center gap-3 my-4">
                            <div className="flex-1 h-px bg-slate-200" />
                            <span className="text-xs text-slate-400 font-medium">or sign in with password</span>
                            <div className="flex-1 h-px bg-slate-200" />
                        </div>
                    </div>
                )}

                <form className="space-y-4" onSubmit={handleLogin}>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Username / Email</label>
                        <input
                            type="text"
                            required
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200"
                            placeholder="admin@example.com"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                        <input
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200"
                            placeholder="••••••••"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            App{' '}
                            <span className="text-slate-400 font-normal">
                                (optional — enables social login / SSO if configured)
                            </span>
                        </label>
                        {apps.length > 0 ? (
                            <select
                                value={appId}
                                onChange={(e) => setAppId(e.target.value)}
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-200"
                            >
                                <option value="">Select app</option>
                                {apps.map((a) => (
                                    <option key={a.app_id} value={a.app_id}>
                                        {a.app_name}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <input
                                type="text"
                                value={appId}
                                onChange={(e) => setAppId(e.target.value)}
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200"
                                placeholder="cf814674-60e5-4e23-965d-c8aab41e78bf"
                            />
                        )}
                    </div>
                    {tenants.length > 0 && (
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                                Tenant <span className="text-slate-400 font-normal">(optional)</span>
                            </label>
                            <select
                                value={tenantId}
                                onChange={(e) => setTenantId(e.target.value)}
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-200"
                            >
                                <option value="">Select tenant</option>
                                {tenants.map((t) => (
                                    <option key={t.tenant_id} value={t.tenant_id}>
                                        {t.tenant_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading || !!socialLoading}
                        className="w-full rounded-lg bg-slate-900 text-white font-semibold py-2.5 hover:bg-slate-800 disabled:opacity-60"
                    >
                        {loading ? 'Signing in...' : 'Sign in'}
                    </button>
                </form>

                {/* Enterprise SSO buttons — shown when app_id has configured SSO providers */}
                {ssoProviders.length > 0 && (
                    <div className="mt-5">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="flex-1 h-px bg-slate-200" />
                            <span className="text-xs text-slate-400 font-medium">or sign in with SSO</span>
                            <div className="flex-1 h-px bg-slate-200" />
                        </div>
                        <div className="space-y-2">
                            {ssoProviders.map((provider) => (
                                <a
                                    key={provider.provider_slug}
                                    href={`/api/sso/login/${provider.provider_slug}?app_id=${encodeURIComponent(
                                        appId
                                    )}`}
                                    className="w-full flex items-center justify-center gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-2.5 text-sm font-medium text-primary-700 hover:bg-primary-100 transition-colors"
                                >
                                    <SSOIcon />
                                    Sign in with {provider.provider_name}
                                    <span className="text-xs text-primary-400 font-normal">
                                        ({provider.protocol.toUpperCase()})
                                    </span>
                                </a>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── SVG Icons ────────────────────────────────────────────────────────────────

const GoogleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
            fill="#4285F4"
        />
        <path
            d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
            fill="#34A853"
        />
        <path
            d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
            fill="#FBBC05"
        />
        <path
            d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
            fill="#EA4335"
        />
    </svg>
);

const AppleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 814 1000" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
        <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-42.4-150.3-119.2c-52.3-89-98-224.2-98-350.2 0-200 131-306.4 260.6-306.4 70.8 0 129.7 46.5 173.6 46.5 42.1 0 109.1-49.1 189.5-49.1 30.4 0 108.1 2.6 168.1 75.7zm-134.3-91.9c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z" />
    </svg>
);

const SSOIcon = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d="M2 12C2 6.5 6.5 2 12 2a10 10 0 0 1 8 4" />
        <path d="M5 19.5C5.5 18 6 15 6 12c0-.7.12-1.37.34-2" />
        <path d="M17.29 21.02c.12-.6.43-2.3.5-3.02" />
        <path d="M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4" />
        <path d="M8.65 22c.21-.66.45-1.32.57-2" />
        <path d="M14 13.12c0 2.38 0 6.38-1 8.88" />
        <path d="M2 16h.01" />
        <path d="M21.8 16c.2-2 .131-5.354 0-6" />
        <path d="M9 6.8a6 6 0 0 1 9 5.2c0 .47 0 1.17-.02 2" />
    </svg>
);

export default LoginPage;
